import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Calendar as CalendarIcon,
  Heart,
  MessageCircle,
  Repeat2,
  Eye,
  Trash2,
  Pencil,
  FileDown,
  ExternalLink,
} from "lucide-react";

const WINDOWS = [
  { k: 7, label: "7d" },
  { k: 30, label: "30d" },
  { k: 90, label: "90d" },
];

export default function Reports() {
  const { workspaceId } = useParams();
  const [tab, setTab] = useState("overview");

  return (
    <Layout
      title="Reports"
    >
      <div className="flex items-center gap-1 mb-5 bg-zinc-900 border border-zinc-800 rounded-md p-1 w-fit" data-testid="reports-tabs">
        {[
          { k: "overview", label: "Overview" },
          { k: "calendar", label: "Calendar" },
          { k: "deliverables", label: "Deliverables" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`reports-tab-${t.k}`}
            className={`text-xs px-3 py-1.5 rounded-sm transition-colors ${
              tab === t.k ? "bg-zinc-800 text-zinc-50" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewPanel workspaceId={workspaceId} />}
      {tab === "calendar" && <CalendarPanel workspaceId={workspaceId} />}
      {tab === "deliverables" && <DeliverablesPanel workspaceId={workspaceId} />}
    </Layout>
  );
}

// ============================================================
// OVERVIEW — engagement summary, top performers, voice breakdown
// ============================================================
function OverviewPanel({ workspaceId }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet(`/workspaces/${workspaceId}/reports/overview`, { days });
      setData(r);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, days]);

  const refreshMetrics = async () => {
    setRefreshing(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/reports/refresh-metrics`, {});
      toast.success(r.message || "Refreshing — give it 1-3 min");
      setTimeout(() => load(), 90000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <div className="overline">loading…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <TabsList className="bg-zinc-900 border border-zinc-800 rounded-md p-1" data-testid="overview-window-tabs">
            {WINDOWS.map((w) => (
              <TabsTrigger
                key={w.k}
                value={String(w.k)}
                data-testid={`window-${w.k}`}
                className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 text-zinc-400 rounded-sm px-3 py-1.5 text-sm"
              >
                {w.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <Button
            onClick={refreshMetrics}
            disabled={refreshing}
            data-testid="refresh-metrics-btn"
            className="btn-ghost"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Pulling…" : "Refresh metrics"}
          </Button>
          <Button
            onClick={() => window.print()}
            data-testid="print-btn"
            className="btn-ghost"
          >
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> Export PDF
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kpi-grid">
        <KpiCard label="posts" value={data.summary.posts} delta={data.deltas.posts} icon={CalendarIcon} testId="kpi-posts" />
        <KpiCard label="likes" value={data.summary.likes} delta={data.deltas.likes} icon={Heart} testId="kpi-likes" />
        <KpiCard label="comments" value={data.summary.comments} delta={data.deltas.comments} icon={MessageCircle} testId="kpi-comments" />
        <KpiCard label="reposts" value={data.summary.reposts} delta={data.deltas.reposts} icon={Repeat2} testId="kpi-reposts" />
        <KpiCard label="views" value={data.summary.views} delta={data.deltas.views} icon={Eye} testId="kpi-views" />
      </div>

      {/* RESPOND reply rate */}
      <div className="surface p-5" data-testid="reply-rate-card">
        <div className="overline mb-3">respond pipeline · last {days}d</div>
        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-3xl heading text-zinc-100">{data.replies.reply_rate_pct}%</div>
            <div className="overline">reply rate</div>
          </div>
          <div className="text-sm text-zinc-400">
            <span className="text-emerald-400">{data.replies.used}</span> replied ·
            <span className="text-zinc-300 ml-1">{data.replies.pending}</span> pending ·
            <span className="text-zinc-500 ml-1">{data.replies.skipped}</span> skipped
          </div>
        </div>
      </div>

      {/* Voice profile breakdown */}
      <div className="surface p-5">
        <div className="overline mb-3">by voice profile</div>
        {data.by_voice.length === 0 ? (
          <div className="text-sm text-zinc-500">No posts in this window.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="voice-breakdown-table">
              <thead>
                <tr className="text-left text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 pr-4 overline">profile</th>
                  <th className="py-2 px-2 overline">posts</th>
                  <th className="py-2 px-2 overline">likes</th>
                  <th className="py-2 px-2 overline">avg likes</th>
                  <th className="py-2 px-2 overline">comments</th>
                  <th className="py-2 px-2 overline">avg comments</th>
                  <th className="py-2 px-2 overline">reposts</th>
                  <th className="py-2 px-2 overline">views</th>
                </tr>
              </thead>
              <tbody>
                {data.by_voice.map((v) => (
                  <tr key={v.voice_profile_id} className="border-b border-zinc-900">
                    <td className="py-2.5 pr-4 text-zinc-200">{v.voice_profile_name}</td>
                    <td className="py-2.5 px-2">{v.posts}</td>
                    <td className="py-2.5 px-2">{v.likes}</td>
                    <td className="py-2.5 px-2 text-zinc-400">{v.avg_likes}</td>
                    <td className="py-2.5 px-2">{v.comments}</td>
                    <td className="py-2.5 px-2 text-zinc-400">{v.avg_comments}</td>
                    <td className="py-2.5 px-2">{v.reposts}</td>
                    <td className="py-2.5 px-2 text-zinc-400">{v.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top performers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="surface p-5" data-testid="top-posts-card">
          <div className="overline mb-3 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> top posts · last {days}d
          </div>
          {data.top_posts.length === 0 ? (
            <div className="text-sm text-zinc-500">No data yet. Hit "Refresh metrics".</div>
          ) : (
            <ul className="space-y-3">
              {data.top_posts.map((p) => (
                <PostRow key={p.id} post={p} />
              ))}
            </ul>
          )}
        </div>

        <div className="surface p-5" data-testid="worst-posts-card">
          <div className="overline mb-3 flex items-center gap-2">
            <TrendingDown className="w-3.5 h-3.5 text-zinc-500" /> under-performers
          </div>
          {data.worst_posts.length === 0 ? (
            <div className="text-sm text-zinc-500">No data yet.</div>
          ) : (
            <ul className="space-y-3">
              {data.worst_posts.map((p) => (
                <PostRow key={p.id} post={p} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, delta, icon: Icon, testId }) {
  const positive = (delta?.diff || 0) >= 0;
  return (
    <div className="surface p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="overline">{label}</span>
        <Icon className="w-3.5 h-3.5 text-zinc-500" />
      </div>
      <div className="text-2xl heading text-zinc-100 mb-1">{(value || 0).toLocaleString()}</div>
      <div className={`text-xs ${positive ? "text-emerald-400" : "text-rose-400"}`}>
        {positive ? "▲" : "▼"} {Math.abs(delta?.diff || 0)} · {delta?.pct ?? 0}% vs prev
      </div>
    </div>
  );
}

function PostRow({ post }) {
  const e = post.engagement || {};
  return (
    <li className="border-b border-zinc-900 last:border-0 pb-3 last:pb-0">
      <div className="flex items-center justify-between gap-3 mb-1">
        <span className="overline text-zinc-500">
          {(post.post_date || "").slice(0, 10)} · {post.voice_profile_name}
        </span>
        {post.post_url && (
          <a
            href={post.post_url}
            target="_blank"
            rel="noreferrer"
            className="text-zinc-500 hover:text-zinc-200"
            data-testid={`open-post-${post.id}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
      <p className="text-sm text-zinc-300 line-clamp-2 mb-2">
        {(post.post_text || "").slice(0, 160)}
        {post.post_text?.length > 160 ? "…" : ""}
      </p>
      <div className="flex items-center gap-3 text-xs text-zinc-400">
        <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" /> {e.likes || 0}</span>
        <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {e.comments || 0}</span>
        <span className="inline-flex items-center gap-1"><Repeat2 className="w-3 h-3" /> {e.reposts || 0}</span>
        <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {e.views || 0}</span>
      </div>
    </li>
  );
}

// ============================================================
// CALENDAR — heatmap of post days
// ============================================================
function CalendarPanel({ workspaceId }) {
  const [days, setDays] = useState(60);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiGet(`/workspaces/${workspaceId}/reports/calendar`, { days })
      .then(setData)
      .finally(() => setLoading(false));
  }, [workspaceId, days]);

  if (loading) return <div className="overline">loading…</div>;
  if (!data) return null;

  // Build a day-by-day grid for the window
  const end = new Date(data.to);
  const grid = [];
  const map = new Map((data.days || []).map((d) => [d.day, d]));
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    grid.push({ day: key, ...(map.get(key) || { posts: 0, likes: 0, comments: 0, views: 0 }) });
  }
  const maxPosts = Math.max(...grid.map((g) => g.posts), 1);

  return (
    <div className="space-y-5">
      <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v))}>
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-md p-1" data-testid="calendar-window-tabs">
          {[
            { k: 30, label: "30d" },
            { k: 60, label: "60d" },
            { k: 90, label: "90d" },
          ].map((w) => (
            <TabsTrigger
              key={w.k}
              value={String(w.k)}
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 text-zinc-400 rounded-sm px-3 py-1.5 text-sm"
            >
              {w.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="surface p-5" data-testid="calendar-heatmap">
        <div className="overline mb-3">posting heatmap · last {days}d</div>
        <div className="grid grid-cols-7 gap-1.5 text-xs">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <div key={`h${i}`} className="text-zinc-500 text-center pb-1">{d}</div>
          ))}
          {/* pad with empty cells so first day aligns to weekday */}
          {(() => {
            const cells = [];
            const firstDay = new Date(grid[0].day + "T00:00:00Z").getUTCDay();
            for (let i = 0; i < firstDay; i++) cells.push(<div key={`p${i}`} />);
            grid.forEach((g) => {
              const intensity = g.posts === 0 ? 0 : 0.3 + 0.7 * (g.posts / maxPosts);
              cells.push(
                <div
                  key={g.day}
                  title={`${g.day} · ${g.posts} posts · ${g.likes} likes · ${g.comments} comments`}
                  data-testid={`day-${g.day}`}
                  className="aspect-square rounded-sm border border-zinc-800"
                  style={{
                    backgroundColor: g.posts === 0 ? "rgb(24 24 27)" : `rgba(56, 189, 248, ${intensity})`,
                  }}
                />
              );
            });
            return cells;
          })()}
        </div>
        <div className="flex items-center gap-2 mt-4 text-xs text-zinc-500">
          <span>less</span>
          {[0, 0.3, 0.55, 0.8, 1].map((v, i) => (
            <div
              key={i}
              className="w-3 h-3 rounded-sm border border-zinc-800"
              style={{
                backgroundColor: v === 0 ? "rgb(24 24 27)" : `rgba(56, 189, 248, ${v})`,
              }}
            />
          ))}
          <span>more</span>
        </div>
      </div>

      <div className="surface p-5" data-testid="day-list">
        <div className="overline mb-3">day-by-day · posts published</div>
        {data.days.length === 0 ? (
          <div className="text-sm text-zinc-500">No posts in this window.</div>
        ) : (
          <ul className="divide-y divide-zinc-900">
            {[...data.days].reverse().slice(0, 30).map((d) => (
              <li key={d.day} className="flex items-center justify-between py-2 text-sm">
                <span className="text-zinc-300">{d.day}</span>
                <div className="flex items-center gap-4 text-xs text-zinc-400">
                  <span>{d.posts} posts</span>
                  <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" /> {d.likes}</span>
                  <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {d.comments}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================
// DELIVERABLES — contracts with balance + rollover
// ============================================================
function DeliverablesPanel({ workspaceId }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet(`/workspaces/${workspaceId}/reports/deliverables`);
      setContracts(r.contracts || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setDialogOpen(true); };

  const removeContract = async (id) => {
    if (!window.confirm("Delete this contract?")) return;
    try {
      await apiDelete(`/workspaces/${workspaceId}/contracts/${id}`);
      toast.success("Contract deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="overline">contracts · auto-tracked from published posts</div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="btn-primary" data-testid="create-contract-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New contract
            </Button>
          </DialogTrigger>
          <ContractDialog
            workspaceId={workspaceId}
            editing={editing}
            onClose={() => setDialogOpen(false)}
            onSaved={() => { setDialogOpen(false); load(); }}
          />
        </Dialog>
      </div>

      {loading ? (
        <div className="overline">loading…</div>
      ) : contracts.length === 0 ? (
        <div className="surface p-12 text-center max-w-xl mx-auto" data-testid="deliverables-empty">
          <div className="overline mb-3">no contracts yet</div>
          <h2 className="heading text-2xl text-zinc-100 mb-2">Track what you owe.</h2>
          <p className="text-sm text-zinc-400 mb-6 max-w-md mx-auto">
            Add a contract (e.g., "Angad · 12 posts / 30 days, billing on the 15th") and the system will automatically count what you've delivered, what's missed, and what rolls into next cycle.
          </p>
          <Button className="btn-primary" onClick={openCreate} data-testid="empty-create-contract-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add first contract
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {contracts.map((c) => (
            <ContractCard
              key={c.contract.id}
              contract={c.contract}
              history={c.history}
              onEdit={() => openEdit(c.contract)}
              onDelete={() => removeContract(c.contract.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ContractCard({ contract, history, onEdit, onDelete }) {
  const current = history && history.length > 0 ? history[history.length - 1] : null;

  return (
    <article className="surface p-5" data-testid={`contract-card-${contract.id}`}>
      <header className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg text-zinc-100">{contract.name}</h3>
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{contract.posts_per_period} posts / {contract.period_days}d</span>
            {contract.voice_profile_id ? (
              <span>· per profile</span>
            ) : (
              <span>· workspace-wide</span>
            )}
            <span>· anchor {(contract.period_start || "").slice(0, 10)}</span>
            {contract.rate_per_period ? (
              <span className="text-emerald-400">· {contract.currency || ""}{contract.rate_per_period.toLocaleString()}</span>
            ) : null}
          </div>
          {contract.notes && (
            <p className="text-xs text-zinc-400 mt-2 max-w-xl">{contract.notes}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="btn-ghost text-xs" data-testid={`edit-contract-${contract.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="btn-ghost text-xs text-rose-400" data-testid={`delete-contract-${contract.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* Current period KPIs */}
      {current && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <KpiSimple label="committed" value={current.effective_quota} sub={`base ${current.committed} ${current.rollover_in !== 0 ? `· rollover ${current.rollover_in > 0 ? "+" : ""}${current.rollover_in}` : ""}`} />
          <KpiSimple label="delivered" value={current.delivered} highlight={current.delivered >= current.effective_quota ? "emerald" : "zinc"} />
          <KpiSimple label="missed" value={current.missed} highlight={current.missed > 0 ? "rose" : "zinc"} />
          <KpiSimple label="surplus" value={current.surplus} highlight={current.surplus > 0 ? "emerald" : "zinc"} />
          <KpiSimple
            label="balance"
            value={(current.balance >= 0 ? "+" : "") + current.balance}
            sub={current.balance >= 0 ? "ahead" : "behind"}
            highlight={current.balance >= 0 ? "emerald" : "rose"}
          />
        </div>
      )}

      {/* History table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid={`history-table-${contract.id}`}>
          <thead>
            <tr className="text-zinc-500 border-b border-zinc-800">
              <th className="text-left py-2 pr-3 overline">period</th>
              <th className="text-left py-2 px-2 overline">window</th>
              <th className="text-right py-2 px-2 overline">committed</th>
              <th className="text-right py-2 px-2 overline">rollover in</th>
              <th className="text-right py-2 px-2 overline">quota</th>
              <th className="text-right py-2 px-2 overline">delivered</th>
              <th className="text-right py-2 px-2 overline">missed</th>
              <th className="text-right py-2 px-2 overline">surplus</th>
              <th className="text-right py-2 pl-2 overline">→ next</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.period_index} className="border-b border-zinc-900 text-zinc-300">
                <td className="py-2 pr-3">#{h.period_index}</td>
                <td className="py-2 px-2 text-zinc-500">{h.start.slice(0, 10)} → {h.end.slice(0, 10)}</td>
                <td className="py-2 px-2 text-right">{h.committed}</td>
                <td className={`py-2 px-2 text-right ${h.rollover_in > 0 ? "text-rose-400" : h.rollover_in < 0 ? "text-emerald-400" : "text-zinc-500"}`}>
                  {h.rollover_in > 0 ? "+" : ""}{h.rollover_in}
                </td>
                <td className="py-2 px-2 text-right">{h.effective_quota}</td>
                <td className="py-2 px-2 text-right">{h.delivered}</td>
                <td className={`py-2 px-2 text-right ${h.missed > 0 ? "text-rose-400" : "text-zinc-500"}`}>{h.missed}</td>
                <td className={`py-2 px-2 text-right ${h.surplus > 0 ? "text-emerald-400" : "text-zinc-500"}`}>{h.surplus}</td>
                <td className={`py-2 pl-2 text-right ${h.rollover_out > 0 ? "text-rose-400" : h.rollover_out < 0 ? "text-emerald-400" : "text-zinc-500"}`}>
                  {h.rollover_out > 0 ? "+" : ""}{h.rollover_out}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function KpiSimple({ label, value, sub, highlight }) {
  const colors = {
    emerald: "text-emerald-400",
    rose: "text-rose-400",
    zinc: "text-zinc-100",
  };
  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-md p-3">
      <div className="overline mb-1">{label}</div>
      <div className={`text-xl heading ${colors[highlight] || "text-zinc-100"}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ContractDialog({ workspaceId, editing, onClose, onSaved }) {
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [name, setName] = useState(editing?.name || "");
  const [voiceProfileId, setVoiceProfileId] = useState(editing?.voice_profile_id || "");
  const [postsPerPeriod, setPostsPerPeriod] = useState(editing?.posts_per_period || 12);
  const [periodDays, setPeriodDays] = useState(editing?.period_days || 30);
  const [periodStart, setPeriodStart] = useState(
    (editing?.period_start || new Date().toISOString().slice(0, 10))
  );
  const [rate, setRate] = useState(editing?.rate_per_period || "");
  const [currency, setCurrency] = useState(editing?.currency || "INR");
  const [notes, setNotes] = useState(editing?.notes || "");
  const [active, setActive] = useState(editing?.active !== false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet(`/workspaces/${workspaceId}/voice_profiles`).then(setVoiceProfiles).catch(() => {});
  }, [workspaceId]);

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    const body = {
      name: name.trim(),
      voice_profile_id: voiceProfileId || null,
      posts_per_period: Number(postsPerPeriod),
      period_days: Number(periodDays),
      period_start: periodStart,
      rate_per_period: rate === "" ? null : Number(rate),
      currency: currency || "INR",
      notes: notes || "",
      active,
    };
    try {
      if (editing) {
        await apiPatch(`/workspaces/${workspaceId}/contracts/${editing.id}`, body);
        toast.success("Contract updated");
      } else {
        await apiPost(`/workspaces/${workspaceId}/contracts`, body);
        toast.success("Contract created");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-zinc-100">
          {editing ? "Edit contract" : "New contract"}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        <div>
          <Label className="text-xs text-zinc-400">name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Angad · 12 posts per cycle"
            className="bg-zinc-900 border-zinc-800 text-zinc-100"
            data-testid="contract-name-input"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">scope</Label>
          <select
            value={voiceProfileId}
            onChange={(e) => setVoiceProfileId(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 px-3 py-2 rounded-md text-sm"
            data-testid="contract-scope-select"
          >
            <option value="">whole workspace (all voice profiles)</option>
            {voiceProfiles.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">posts per cycle</Label>
            <Input
              type="number"
              min="1"
              value={postsPerPeriod}
              onChange={(e) => setPostsPerPeriod(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              data-testid="contract-posts-input"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">cycle length (days)</Label>
            <Input
              type="number"
              min="1"
              value={periodDays}
              onChange={(e) => setPeriodDays(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              data-testid="contract-period-days-input"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">cycle anchor date (when does the cycle begin?)</Label>
          <Input
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            className="bg-zinc-900 border-zinc-800 text-zinc-100"
            data-testid="contract-anchor-input"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label className="text-xs text-zinc-400">rate per cycle (optional)</Label>
            <Input
              type="number"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="e.g. 30000"
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              data-testid="contract-rate-input"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">currency</Label>
            <Input
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              placeholder="INR"
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              data-testid="contract-currency-input"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">notes</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="bg-zinc-900 border-zinc-800 text-zinc-100"
            data-testid="contract-notes-input"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            data-testid="contract-active-input"
          />
          Active
        </label>
        <div className="flex justify-end gap-2 pt-3">
          <button onClick={onClose} className="btn-ghost" data-testid="contract-cancel-btn">Cancel</button>
          <Button onClick={submit} disabled={busy} className="btn-primary" data-testid="contract-submit-btn">
            {busy ? "Saving…" : (editing ? "Save" : "Create")}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}
